/**
 * Serializable geometry model and editing commands for the Roof Geometry editor.
 *
 * Coordinates remain plan millimetres. The frontend records user intent only;
 * geometry validity, area and perimeter remain backend-authoritative.
 */

import type {
  OpeningGeometry, Point2D, Polygon2D, RoofEdgeProperties, RoofPlanGeometry,
} from '../../gen/ejot/roofing/v1/types';

export interface EditorPoint {
  id: string;
  x: number;
  y: number;
  locked: boolean;
}

export interface EditorOpening {
  id: string;
  displayName: string;
  points: EditorPoint[];
  excludedFromNetArea: boolean;
}

export interface EditorEdge {
  segmentId: string;
  edgeType: RoofEdgeProperties['edge_type'];
  excludedFromZoneGeneration: boolean;
  perimeterFixingEnabled: boolean;
}

export interface GeometryModel {
  points: EditorPoint[];
  closed: boolean;
  openings: EditorOpening[];
  edges: EditorEdge[];
  northOrientationDegrees: number;
  revision: number;
}

export type GeometryOwner =
  | { kind: 'roof' }
  | { kind: 'opening'; openingId: string };

export function emptyModel(): GeometryModel {
  return {
    points: [],
    closed: false,
    openings: [],
    edges: [],
    northOrientationDegrees: 0,
    revision: 0,
  };
}

let idCounter = 0;

export function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

export function resetIdCounter(): void {
  idCounter = 0;
}

export function segmentIdFor(model: GeometryModel, index: number): string {
  const start = model.points[index];
  return start ? `segment-${start.id}` : `segment-${index}`;
}

function point(x: number, y: number): EditorPoint {
  return { id: nextId('point'), x, y, locked: false };
}

export function pointsFor(model: GeometryModel, owner: GeometryOwner): readonly EditorPoint[] {
  if (owner.kind === 'roof') return model.points;
  return model.openings.find((opening) => opening.id === owner.openingId)?.points ?? [];
}

export function allPoints(model: GeometryModel): EditorPoint[] {
  return [...model.points, ...model.openings.flatMap((opening) => opening.points)];
}

export function findPoint(
  model: GeometryModel,
  owner: GeometryOwner,
  pointId: string,
): EditorPoint | undefined {
  return pointsFor(model, owner).find((candidate) => candidate.id === pointId);
}

function replaceOwnerPoints(
  model: GeometryModel,
  owner: GeometryOwner,
  points: EditorPoint[],
): GeometryModel {
  if (owner.kind === 'roof') return { ...model, points };
  return {
    ...model,
    openings: model.openings.map((opening) => opening.id === owner.openingId
      ? { ...opening, points }
      : opening),
  };
}

export function addPoint(model: GeometryModel, x: number, y: number): GeometryModel {
  if (model.closed) return model;
  return { ...model, points: [...model.points, point(x, y)] };
}

export function moveVertex(
  model: GeometryModel,
  owner: GeometryOwner,
  pointId: string,
  x: number,
  y: number,
): GeometryModel {
  const source = pointsFor(model, owner);
  let changed = false;
  const points = source.map((candidate) => {
    if (candidate.id !== pointId || candidate.locked) return candidate;
    if (candidate.x === x && candidate.y === y) return candidate;
    changed = true;
    return { ...candidate, x, y };
  });
  return changed ? replaceOwnerPoints(model, owner, points) : model;
}

/** Backward-compatible outer-boundary command. */
export function movePoint(model: GeometryModel, id: string, x: number, y: number): GeometryModel {
  return moveVertex(model, { kind: 'roof' }, id, x, y);
}

function edgeSettingsEqual(a: EditorEdge, b: EditorEdge): boolean {
  return a.edgeType === b.edgeType
    && a.excludedFromZoneGeneration === b.excludedFromZoneGeneration
    && a.perimeterFixingEnabled === b.perimeterFixingEnabled;
}

/**
 * Deletes an editable vertex without silently collapsing a closed polygon.
 *
 * For the roof boundary, deleting a vertex merges two edges. If both edges
 * have explicit but different metadata, the command is rejected rather than
 * silently choosing one configuration. A split edge inherits its metadata in
 * insertVertex(), so the reverse operation is deterministic.
 */
export function deleteVertex(
  model: GeometryModel,
  owner: GeometryOwner,
  pointId: string,
): GeometryModel {
  const source = pointsFor(model, owner);
  if (source.length <= 3 || !source.some((candidate) => candidate.id === pointId)) return model;

  if (owner.kind === 'opening') {
    return replaceOwnerPoints(model, owner, source.filter((candidate) => candidate.id !== pointId));
  }

  const index = model.points.findIndex((candidate) => candidate.id === pointId);
  if (index < 0) return model;
  const previous = model.points[(index - 1 + model.points.length) % model.points.length];
  const outgoing = model.edges.find((edge) => edge.segmentId === `segment-${pointId}`);
  const incoming = previous
    ? model.edges.find((edge) => edge.segmentId === `segment-${previous.id}`)
    : undefined;

  if (incoming && outgoing && !edgeSettingsEqual(incoming, outgoing)) return model;

  const points = model.points.filter((candidate) => candidate.id !== pointId);
  let edges = model.edges.filter((edge) => edge.segmentId !== `segment-${pointId}`);
  if (!incoming && outgoing && previous) {
    edges = [
      ...edges,
      { ...outgoing, segmentId: `segment-${previous.id}` },
    ];
  }

  return { ...model, points, edges };
}

/**
 * Legacy outer-boundary delete retained for compatibility with stored/editor
 * behavior. New UI commands use deleteVertex(), which keeps closed polygons at
 * three or more vertices.
 */
export function deletePoint(model: GeometryModel, id: string): GeometryModel {
  const points = model.points.filter((candidate) => candidate.id !== id);
  const removed = `segment-${id}`;
  return {
    ...model,
    points,
    edges: model.edges.filter((edge) => edge.segmentId !== removed),
    closed: model.closed && points.length >= 3,
  };
}

export function insertVertex(
  model: GeometryModel,
  owner: GeometryOwner,
  index: number,
  location?: { x: number; y: number },
): GeometryModel {
  const source = pointsFor(model, owner);
  const start = source[index];
  const end = source[(index + 1) % source.length];
  if (!start || !end) return model;

  const inserted = point(
    location?.x ?? (start.x + end.x) / 2,
    location?.y ?? (start.y + end.y) / 2,
  );
  const points = [...source];
  points.splice(index + 1, 0, inserted);
  let next = replaceOwnerPoints(model, owner, points);

  if (owner.kind === 'roof') {
    const sourceEdge = model.edges.find((edge) => edge.segmentId === `segment-${start.id}`);
    if (sourceEdge) {
      next = {
        ...next,
        edges: [
          ...next.edges,
          { ...sourceEdge, segmentId: `segment-${inserted.id}` },
        ],
      };
    }
  }
  return next;
}

/** Backward-compatible midpoint insert for the roof boundary. */
export function insertPointOnSegment(model: GeometryModel, index: number): GeometryModel {
  return insertVertex(model, { kind: 'roof' }, index);
}

export function closeOutline(model: GeometryModel): GeometryModel {
  return model.points.length >= 3 ? { ...model, closed: true } : model;
}

export function reopenOutline(model: GeometryModel): GeometryModel {
  return { ...model, closed: false };
}

export function setRoofPolygon(
  model: GeometryModel,
  coordinates: readonly { x: number; y: number }[],
): GeometryModel {
  if (coordinates.length < 3) return model;
  return {
    ...model,
    points: coordinates.map(({ x, y }) => point(x, y)),
    closed: true,
    edges: [],
  };
}

export function setRectangle(model: GeometryModel, width: number, height: number): GeometryModel {
  return setRoofPolygon(model, [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ]);
}

export function translate(model: GeometryModel, dx: number, dy: number): GeometryModel {
  return {
    ...model,
    points: model.points.map((candidate) => ({ ...candidate, x: candidate.x + dx, y: candidate.y + dy })),
    openings: model.openings.map((opening) => ({
      ...opening,
      points: opening.points.map((candidate) => ({
        ...candidate,
        x: candidate.x + dx,
        y: candidate.y + dy,
      })),
    })),
  };
}

export function translateOpening(
  model: GeometryModel,
  openingId: string,
  dx: number,
  dy: number,
): GeometryModel {
  if (dx === 0 && dy === 0) return model;
  let changed = false;
  const openings = model.openings.map((opening) => {
    if (opening.id !== openingId) return opening;
    changed = true;
    return {
      ...opening,
      points: opening.points.map((candidate) => ({
        ...candidate,
        x: candidate.x + dx,
        y: candidate.y + dy,
      })),
    };
  });
  return changed ? { ...model, openings } : model;
}

/** Retained for compatibility outside Roof Geometry. Site Layout owns project placement. */
export function rotate(model: GeometryModel, degrees: number): GeometryModel {
  const centre = boundsCentre(model);
  const radians = (degrees * Math.PI) / 180;
  const sin = Math.sin(radians);
  const cos = Math.cos(radians);
  const turn = (candidate: EditorPoint): EditorPoint => {
    const dx = candidate.x - centre.x;
    const dy = candidate.y - centre.y;
    return {
      ...candidate,
      x: centre.x + dx * cos - dy * sin,
      y: centre.y + dx * sin + dy * cos,
    };
  };
  return {
    ...model,
    points: model.points.map(turn),
    openings: model.openings.map((opening) => ({ ...opening, points: opening.points.map(turn) })),
  };
}

export function mirror(model: GeometryModel, axis: 'horizontal' | 'vertical'): GeometryModel {
  const centre = boundsCentre(model);
  const flip = (candidate: EditorPoint): EditorPoint => axis === 'horizontal'
    ? { ...candidate, x: 2 * centre.x - candidate.x }
    : { ...candidate, y: 2 * centre.y - candidate.y };
  return {
    ...model,
    points: model.points.map(flip),
    openings: model.openings.map((opening) => ({ ...opening, points: opening.points.map(flip) })),
  };
}

export function centreOnOrigin(model: GeometryModel): GeometryModel {
  const { min } = bounds(model);
  return translate(model, -min.x, -min.y);
}

export function addPolygonOpening(
  model: GeometryModel,
  coordinates: readonly { x: number; y: number }[],
): GeometryModel {
  if (!model.closed || coordinates.length < 3) return model;
  const opening: EditorOpening = {
    id: nextId('opening'),
    displayName: `Opening ${model.openings.length + 1}`,
    excludedFromNetArea: true,
    points: coordinates.map(({ x, y }) => point(x, y)),
  };
  return { ...model, openings: [...model.openings, opening] };
}

export function addRectangularOpening(
  model: GeometryModel,
  x: number,
  y: number,
  width: number,
  height: number,
): GeometryModel {
  if (width === 0 || height === 0) return model;
  return addPolygonOpening(model, [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ]);
}

export function deleteOpening(model: GeometryModel, id: string): GeometryModel {
  const openings = model.openings.filter((opening) => opening.id !== id);
  return openings.length === model.openings.length ? model : { ...model, openings };
}

export function setEdge(model: GeometryModel, edge: EditorEdge): GeometryModel {
  const others = model.edges.filter((candidate) => candidate.segmentId !== edge.segmentId);
  return { ...model, edges: [...others, edge] };
}

export function edgeFor(model: GeometryModel, segmentId: string): EditorEdge {
  return model.edges.find((edge) => edge.segmentId === segmentId) ?? {
    segmentId,
    edgeType: 'ROOF_EDGE_TYPE_UNSPECIFIED',
    excludedFromZoneGeneration: false,
    perimeterFixingEnabled: false,
  };
}

export function bounds(model: GeometryModel): {
  min: { x: number; y: number };
  max: { x: number; y: number };
} {
  const all = allPoints(model);
  if (all.length === 0) return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } };
  return {
    min: { x: Math.min(...all.map((candidate) => candidate.x)), y: Math.min(...all.map((candidate) => candidate.y)) },
    max: { x: Math.max(...all.map((candidate) => candidate.x)), y: Math.max(...all.map((candidate) => candidate.y)) },
  };
}

function boundsCentre(model: GeometryModel): { x: number; y: number } {
  const { min, max } = bounds(model);
  return { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2 };
}

export function segmentLength(a: EditorPoint, b: EditorPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function toContract(model: GeometryModel): RoofPlanGeometry {
  const points: Point2D[] = model.points.map((candidate) => ({
    id: candidate.id,
    x: candidate.x,
    y: candidate.y,
    locked: candidate.locked,
  }));
  const segments = model.points.map((candidate, index) => ({
    id: `segment-${candidate.id}`,
    start_point_id: candidate.id,
    end_point_id: model.points[(index + 1) % model.points.length]?.id ?? candidate.id,
  }));
  const outline: Polygon2D = { points, segments };

  const openings: OpeningGeometry[] = model.openings.map((opening) => ({
    id: opening.id,
    display_name: opening.displayName,
    opening_type: 'OPENING_TYPE_GENERIC',
    excluded_from_net_area: opening.excludedFromNetArea,
    outline: {
      points: opening.points.map((candidate) => ({
        id: candidate.id,
        x: candidate.x,
        y: candidate.y,
        locked: candidate.locked,
      })),
      segments: opening.points.map((candidate, index) => ({
        id: `segment-${candidate.id}`,
        start_point_id: candidate.id,
        end_point_id: opening.points[(index + 1) % opening.points.length]?.id ?? candidate.id,
      })),
    },
  }));

  const edge_properties: RoofEdgeProperties[] = model.edges.map((edge) => ({
    segment_id: edge.segmentId,
    edge_type: edge.edgeType,
    excluded_from_zone_generation: edge.excludedFromZoneGeneration,
    perimeter_fixing_enabled: edge.perimeterFixingEnabled,
  }));

  return {
    outline,
    openings,
    edge_properties,
    north_orientation: { degrees: model.northOrientationDegrees },
    unit_system: 'UNIT_SYSTEM_METRIC',
    revision: model.revision,
    editor_version: EDITOR_VERSION,
  };
}

export function fromContract(geometry: RoofPlanGeometry | undefined): GeometryModel {
  if (!geometry?.outline?.points?.length) return emptyModel();

  const points: EditorPoint[] = geometry.outline.points.map((candidate) => ({
    id: candidate.id ?? nextId('point'),
    x: candidate.x ?? 0,
    y: candidate.y ?? 0,
    locked: candidate.locked ?? false,
  }));

  return {
    points,
    closed: points.length >= 3,
    openings: (geometry.openings ?? []).map((opening) => ({
      id: opening.id ?? nextId('opening'),
      displayName: opening.display_name ?? '',
      excludedFromNetArea: opening.excluded_from_net_area ?? false,
      points: (opening.outline?.points ?? []).map((candidate) => ({
        id: candidate.id ?? nextId('point'),
        x: candidate.x ?? 0,
        y: candidate.y ?? 0,
        locked: candidate.locked ?? false,
      })),
    })),
    edges: (geometry.edge_properties ?? []).map((edge) => ({
      segmentId: edge.segment_id ?? '',
      edgeType: edge.edge_type ?? 'ROOF_EDGE_TYPE_UNSPECIFIED',
      excludedFromZoneGeneration: edge.excluded_from_zone_generation ?? false,
      perimeterFixingEnabled: edge.perimeter_fixing_enabled ?? false,
    })),
    northOrientationDegrees: geometry.north_orientation?.degrees ?? 0,
    revision: geometry.revision ?? 0,
  };
}

export const EDITOR_VERSION = '2';
